import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart, MapPin, Star, Trash2, ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import LoadingScreen from '../components/LoadingScreen';
import { useAuth } from '../context/AuthContext';
import { formatDOP } from '../lib/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientFavorites() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [removing, setRemoving] = useState(null);
  const [toast, setToast] = useState(null);
  // Nombres de categoría desde el admin (antes: diccionario hardcoded que se
  // desincronizaba cada vez que se creaba una categoría nueva en AdminCategories).
  const [categoryNames, setCategoryNames] = useState({});

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/categories`)
      .then(r => r.json())
      .then(cats => setCategoryNames(Object.fromEntries((cats || []).map(c => [c.type, c.name]))))
      .catch(() => {});
  }, []);

  const showToast = (type, msg) => { setToast({ type, msg }); setTimeout(() => setToast(null), 3000); };

  const fetchFavorites = () => {
    fetch(`${BACKEND_URL}/api/client/favorites`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => { setFavorites(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => navigate('/login'));
  };

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, authLoading]);

  const removeFavorite = async (assetId) => {
    setRemoving(assetId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/client/favorites/${assetId}`, { method: 'DELETE', credentials: 'include' });
      if (res.ok) {
        setFavorites(prev => prev.filter(f => f.asset_id !== assetId));
        showToast('success', t('clientFavorites.removedToast'));
      }
    } catch { showToast('error', t('clientFavorites.connectionError')); }
    finally { setRemoving(null); }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <LoadingScreen inline className="min-h-[70vh]" message={t('clientFavorites.loading')} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-5 py-3 rounded-2xl shadow-xl text-white font-medium text-sm ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-500'}`}>
          {toast.msg}
        </div>
      )}

      <div className="container mx-auto px-4 md:px-6 max-w-5xl py-8 pb-24 md:pb-8">
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/client/dashboard')} aria-label={t('clientFavorites.goBack')} className="p-2 border border-gray-200 rounded-xl hover:bg-gray-50 transition">
            <ArrowLeft className="w-5 h-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-2">
              <Heart className="w-7 h-7 text-red-500 fill-red-500" /> {t('clientFavorites.title')}
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {t('clientFavorites.activeCount', { count: favorites.length })}
            </p>
          </div>
        </div>

        {favorites.length === 0 ? (
          <div className="bg-white rounded-3xl p-16 shadow-sm text-center">
            <Heart className="w-16 h-16 text-gray-200 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-700 mb-2">{t('clientFavorites.emptyTitle')}</h2>
            <p className="text-gray-500 mb-6">{t('clientFavorites.emptyMessage')}</p>
            <button onClick={() => navigate('/')} className="bg-[#134168] text-white px-6 py-3 rounded-full font-bold hover:bg-[#39b4aa] transition">
              {t('clientFavorites.exploreVehicles')}
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {favorites.map(fav => (
              <div key={fav.favorite_id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-md transition group">
                <div className="relative">
                  <img
                    src={fav.image || `${process.env.PUBLIC_URL}/images/placeholder.jpg`}
                    alt={fav.name}
                    loading="lazy"
                    className="w-full h-44 object-cover cursor-pointer"
                    onClick={() => navigate(`/listing/${fav.asset_id}`)}
                  />
                  {!fav.is_active && (
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <span className="bg-white text-gray-700 text-xs font-bold px-3 py-1 rounded-full">{t('clientFavorites.notAvailable')}</span>
                    </div>
                  )}
                  <button
                    onClick={() => removeFavorite(fav.asset_id)}
                    disabled={removing === fav.asset_id}
                    className="absolute top-3 right-3 p-2 bg-white/95 rounded-full shadow-md hover:bg-red-50 transition opacity-100 sm:opacity-0 sm:group-hover:opacity-100 disabled:opacity-50"
                    title={t('clientFavorites.removeFavorite')}
                    aria-label={t('clientFavorites.removeFavorite')}
                  >
                    <Trash2 className="w-4 h-4 text-red-500" />
                  </button>
                </div>
                <div className="p-4">
                  <span className="text-xs text-gray-400 uppercase tracking-wide font-medium">{categoryNames[fav.type] || fav.type}</span>
                  <h3 className="font-bold text-base leading-tight mt-0.5 mb-1 cursor-pointer hover:text-[#134168]" onClick={() => navigate(`/listing/${fav.asset_id}`)}>
                    {fav.name}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-3">
                    <MapPin className="w-3.5 h-3.5" /> {fav.location_zone}
                    {fav.rating > 0 && <><Star className="w-3.5 h-3.5 text-yellow-400 fill-yellow-400 ml-2" /> {fav.rating?.toFixed(1)}</>}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className="text-[#134168] font-bold">{formatDOP(fav.price_per_day)}{t('listing.perDayShort2', '/día')}</span>
                      {fav.price_per_hour && <span className="text-gray-400 text-xs ml-2">{formatDOP(fav.price_per_hour)}{t('listing.perHourShort', '/ hora')}</span>}
                    </div>
                    <button
                      onClick={() => navigate(`/listing/${fav.asset_id}`)}
                      disabled={!fav.is_active}
                      className="px-4 py-1.5 bg-[#134168] text-white rounded-full text-sm font-semibold hover:bg-[#39b4aa] transition disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {t('clientFavorites.reserve')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
